import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { FaCalendarAlt, FaMoneyBillWave, FaHotel, FaChartBar, FaChartPie, FaFilter } from 'react-icons/fa';
import { exportToExcel } from '../utils/exportToExcel';
import { reportsAPI, hotelsAPI, bookingsAPI } from '../services/api';
import useApi from '../hooks/useApi';
import './Dashboard.css';

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState({
    totalBookings: 0,
    totalBookingsChange: '+0%',
    totalProfit: 0,
    totalHotels: 0,
    newHotelsCount: 0,
    mostSearched: [],
    mostBooked: [],
    bookingsByCountry: [],
    recentBookings: []
  });

  // Profit state
  const [profitData, setProfitData] = useState({
    summary: {
      total_revenue: 0,
      total_commission: 0,
      total_profit: 0,
      booking_count: 0
    },
    profit_by_hotel: [],
    bookings: []
  });
  
  // Profit filters
  const [profitFilters, setProfitFilters] = useState({
    month: '',
    year: '',
    hotel_id: ''
  });
  
  // Hotels for dropdown
  const [hotels, setHotels] = useState([]);

  // Use API hooks for dashboard data
  const { data: bookingsByCountryData, loading: bookingsLoading } = useApi(() => reportsAPI.getBookingsByCountry('2025-10-01', '2025-10-31'));
  const { data: revenueByHotelData, loading: revenueLoading } = useApi(() => reportsAPI.getRevenueByHotel('2025-10-01', '2025-10-31'));
  const { data: bookingTrendsData, loading: trendsLoading } = useApi(() => reportsAPI.getBookingTrends('2025-10-01', '2025-10-31'));
  const { data: userDemographicsData, loading: demographicsLoading } = useApi(() => reportsAPI.getUserDemographics('2025-10-01', '2025-10-31'));
  
  // Profit API hook - Only fetch when explicitly triggered
  console.log('Profit Filters:', profitFilters);
  
  // Store the filters to apply
  const [appliedFilters, setAppliedFilters] = useState({
    month: '',
    year: '',
    hotel_id: ''
  });
  
  // API call that uses applied filters
  const { data: profitAPIData, loading: profitLoading, refetch: refetchProfit, error: profitError } = useApi(() => {
    console.log('Making profit API call with applied filters:', appliedFilters);
    return reportsAPI.getProfit({
      month: appliedFilters.month || undefined,
      year: appliedFilters.year || undefined,
      hotel_id: appliedFilters.hotel_id || undefined
    });
  }, [appliedFilters.month, appliedFilters.year, appliedFilters.hotel_id]);

  // Set dashboard data when API responses are received
  useEffect(() => {
    if (bookingsByCountryData) {
      setDashboardData(prev => ({
        ...prev,
        bookingsByCountry: bookingsByCountryData || []
      }));
    }
  }, [bookingsByCountryData]);

  useEffect(() => {
    if (revenueByHotelData) {
      // Calculate total revenue and hotels from revenue data
      const totalRevenue = revenueByHotelData.reduce((sum, hotel) => sum + (hotel.revenue || 0), 0);
      const totalHotels = revenueByHotelData.length;
      
      setDashboardData(prev => ({
        ...prev,
        totalProfit: totalRevenue,
        totalHotels: totalHotels,
        mostBooked: revenueByHotelData || []
      }));
    }
  }, [revenueByHotelData]);

  useEffect(() => {
    if (bookingTrendsData) {
      // Calculate total bookings from trends data
      const totalBookings = bookingTrendsData.reduce((sum, day) => sum + (day.bookings || 0), 0);
      
      setDashboardData(prev => ({
        ...prev,
        totalBookings: totalBookings,
        mostSearched: bookingTrendsData || []
      }));
    }
  }, [bookingTrendsData]);

  useEffect(() => {
    if (userDemographicsData) {
      setDashboardData(prev => ({
        ...prev,
        userDemographics: userDemographicsData || []
      }));
    }
  }, [userDemographicsData]);

  // Update profit data when API response is received
  useEffect(() => {
    console.log('=== PROFIT DATA UPDATE USEEFFECT RUNNING ===');
    console.log('profitAPIData:', profitAPIData);
    console.log('profitLoading:', profitLoading);
    console.log('profitError:', profitError);
    console.log('Type of profitAPIData:', typeof profitAPIData);
    console.log('Is profitAPIData null/undefined?', profitAPIData == null);
    
    if (profitError) {
      console.error('Profit API Error:', profitError);
      return;
    }
    
    if (profitAPIData) {
      console.log('Raw API Response:', profitAPIData);
      console.log('Type of profitAPIData:', typeof profitAPIData);
      console.log('Keys in profitAPIData:', Object.keys(profitAPIData));
      
      // Check if this is the raw response or already extracted data
      let profitDataToUse;
      if (profitAPIData.success === true && profitAPIData.data) {
        // This is the raw API response format
        profitDataToUse = profitAPIData.data;
        console.log('Using raw response data:', profitDataToUse);
      } else if (profitAPIData.summary) {
        // This is already extracted data from useApi
        profitDataToUse = profitAPIData;
        console.log('Using extracted data:', profitDataToUse);
      } else {
        console.log('Unexpected data structure:', profitAPIData);
        return;
      }
      
      if (profitDataToUse) {
        console.log('API Data Structure:', profitDataToUse);
        console.log('Summary Data:', profitDataToUse.summary);
        console.log('Profit by hotel data:', profitDataToUse.profit_by_hotel);
        console.log('Profit by hotel type:', typeof profitDataToUse.profit_by_hotel);
        console.log('Profit by hotel isArray:', Array.isArray(profitDataToUse.profit_by_hotel));
        
        // Force a complete state refresh
        const newProfitData = {
          summary: {
            total_revenue: parseFloat(profitDataToUse.summary?.total_revenue) || 0,
            total_commission: parseFloat(profitDataToUse.summary?.total_commission) || 0,
            total_profit: parseFloat(profitDataToUse.summary?.total_profit) || 0,
            booking_count: parseInt(profitDataToUse.summary?.booking_count) || 0
          },
          profit_by_hotel: Array.isArray(profitDataToUse.profit_by_hotel) ? profitDataToUse.profit_by_hotel : [],
          bookings: profitDataToUse.bookings || []
        };
        
        console.log('=== SETTING NEW PROFIT STATE ===');
        console.log('New Profit Data:', newProfitData);
        console.log('Values to set:', {
          revenue: newProfitData.summary.total_revenue,
          profit: newProfitData.summary.total_profit,
          bookings: newProfitData.summary.booking_count
        });
        
        // Force state update with new object reference
        console.log('=== FORCING STATE UPDATE ===');
        
        // Create completely new object to force re-render
        const forcedProfitData = JSON.parse(JSON.stringify(newProfitData));
        
        console.log('Forced update data:', forcedProfitData);
        console.log('Values being set:', {
          revenue: forcedProfitData.summary.total_revenue,
          profit: forcedProfitData.summary.total_profit,
          bookings: forcedProfitData.summary.booking_count
        });
        
        setProfitData(forcedProfitData);
        
        // Update dashboard data with the booking count from profit data
        // Calculate percentage change based on previous data if available
        setDashboardData(prev => {
          const currentBookings = forcedProfitData.summary.booking_count;
          const previousBookings = prev.totalBookings || 0;
          
          // Calculate percentage change
          let percentageChange = '+0%';
          if (previousBookings > 0) {
            const change = ((currentBookings - previousBookings) / previousBookings) * 100;
            percentageChange = change >= 0 ? `+${change.toFixed(1)}%` : `${change.toFixed(1)}%`;
          } else {
            // If no previous data, we can't calculate change, so use a default or skip
            percentageChange = currentBookings > 0 ? '+100%' : '+0%';
          }
          
          return {
            ...prev,
            totalBookings: currentBookings,
            totalBookingsChange: percentageChange
          };
        });
        
        // Also update a separate debug state to see if it works
        window.debugProfitData = forcedProfitData;
        console.log('Window debug data set:', window.debugProfitData);
      } else {
        console.log('profitDataToUse is empty');
      }
    } else {
      console.log('No profit API data received');
    }
  }, [profitAPIData]); // Simplified dependencies

  // Fetch hotels for dropdown
  useEffect(() => {
    const fetchHotels = async () => {
      try {
        const response = await hotelsAPI.getAll();
        console.log('Hotels API Response:', response);
        const hotelsData = response.data?.data?.hotels || response.data || [];
        
        // Calculate total hotels
        const totalHotels = hotelsData.length;
        
        // Calculate new hotels in the last two weeks
        const twoWeeksAgo = new Date();
        twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
        
        const newHotels = hotelsData.filter(hotel => {
          const createdAt = new Date(hotel.created_at || hotel.created || hotel.date_created || hotel.updated_at || '1970-01-01');
          return createdAt >= twoWeeksAgo;
        });
        
        const newHotelsCount = newHotels.length;
        
        setHotels(hotelsData);
        console.log('Hotels set:', hotelsData);
        
        // Update dashboard data
        setDashboardData(prev => ({
          ...prev,
          totalHotels: totalHotels,
          newHotelsCount: newHotelsCount
        }));
        
      } catch (error) {
        console.error('Error fetching hotels:', error);
      }
    };
    
    fetchHotels();
  }, []);
  
  // State for recent bookings
  const [recentBookingsState, setRecentBookingsState] = useState({
    data: [],
    loading: true,
    error: null
  });
  
  // Fetch recent bookings using the same approach as Bookings.js
  useEffect(() => {
    const fetchRecentBookings = async () => {
      try {
        setRecentBookingsState(prev => ({ ...prev, loading: true, error: null }));
        
        const bookingsResponse = await bookingsAPI.getAll();
        console.log('Dashboard - Raw bookings API response:', bookingsResponse);
        
        // Use the same approach as Bookings.js: bookingsResponse.data?.data.bookings || bookingsResponse.data || []
        const bookingsArray = bookingsResponse.data?.data?.bookings || 
                             bookingsResponse.data?.bookings || 
                             bookingsResponse.data || 
                             [];
        
        console.log('Dashboard - Extracted bookings array:', bookingsArray);
        console.log('Dashboard - Booking array length:', bookingsArray.length);
        
        // Check if we have valid bookings data
        if (!Array.isArray(bookingsArray) || bookingsArray.length === 0) {
          setDashboardData(prev => ({
            ...prev,
            recentBookings: []
          }));
          setRecentBookingsState({ data: [], loading: false, error: null });
          return;
        }
        
        // Sort by date to get most recent first
        const sortedBookings = [...bookingsArray]
          .sort((a, b) => {
            // Try multiple possible date fields in order of preference
            const dateA = new Date(
              a.created_at || 
              a.booking_date || 
              a.created || 
              a.date || 
              a.updated_at || 
              '1970-01-01'
            );
            const dateB = new Date(
              b.created_at || 
              b.booking_date || 
              b.created || 
              b.date || 
              b.updated_at || 
              '1970-01-01'
            );
            
            // Handle invalid dates
            if (isNaN(dateA.getTime())) return 1;
            if (isNaN(dateB.getTime())) return -1;
            
            return dateB - dateA; // Descending order (most recent first)
          })
          .slice(0, 5); // Get only the 5 most recent
        
        console.log('Dashboard - Sorted bookings:', sortedBookings);
        
        // Transform booking data to match the expected format in the table
        const transformedBookings = sortedBookings.map(booking => ({
          id: booking.booking_reference || booking.id || booking.booking_id || `#BK${booking.id || Math.floor(Math.random() * 10000)}`,
          customer: booking.customer_name || booking.customer || booking.user?.name || 'N/A',
          hotel: booking.hotel_name || booking.hotel || 'N/A',
          date: booking.booking_date || booking.created_at || booking.created || booking.date || 'N/A',
          amount: parseFloat(booking.total_price || booking.amount || booking.total_amount || booking.client_price || 0) || 0,
          status: booking.status || 'Pending'
        }));
        
        console.log('Dashboard - Transformed bookings:', transformedBookings);
        
        setDashboardData(prev => ({
          ...prev,
          recentBookings: transformedBookings
        }));
        
        setRecentBookingsState({ data: transformedBookings, loading: false, error: null });
      } catch (error) {
        console.error('Error fetching recent bookings:', error);
        setDashboardData(prev => ({
          ...prev,
          recentBookings: []
        }));
        setRecentBookingsState({ data: [], loading: false, error: error.message });
      }
    };
    
    fetchRecentBookings();
  }, []); // Empty dependency array like in Bookings.js
  
  const recentBookingsLoading = recentBookingsState.loading;
  const recentBookingsError = recentBookingsState.error;

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  // Profit filter handlers
  const handleProfitFilterChange = (field, value) => {
    setProfitFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const applyProfitFilters = () => {
    console.log('Applying filters:', profitFilters);
    
    setAppliedFilters({
      month: profitFilters.month,
      year: profitFilters.year,
      hotel_id: profitFilters.hotel_id  // Send hotel_id as per API requirement
    });
  };

  const clearProfitFilters = () => {
    setProfitFilters({
      month: '',
      year: '',
      hotel_id: ''
    });
    setAppliedFilters({
      month: '',
      year: '',
      hotel_id: ''
    });
  };

  // Function to get appropriate chart data based on filters
  const getChartData = () => {
    // If we have profit_by_hotel data (multiple hotels comparison)
    if (profitData.profit_by_hotel && Array.isArray(profitData.profit_by_hotel) && profitData.profit_by_hotel.length > 0) {
      return profitData.profit_by_hotel.filter(hotel => hotel.hotel_id !== null && hotel.total_profit !== null && hotel.total_profit !== undefined);
    }
    
    // If we're filtering for a specific hotel, create a single-item array from the summary
    if (appliedFilters.hotel_id) {
      // Find the hotel name from the hotels list
      const selectedHotel = hotels.find(hotel => hotel.id == appliedFilters.hotel_id);
      const hotelName = selectedHotel?.name_en || selectedHotel?.name || selectedHotel?.hotel_name || `Hotel ${appliedFilters.hotel_id}`;
      
      // Create a single data point for the chart
      return [{
        hotel_id: appliedFilters.hotel_id,
        hotel_name: hotelName,
        total_profit: profitData.summary?.total_profit || 0,
        total_revenue: profitData.summary?.total_revenue || 0,
        booking_count: profitData.summary?.booking_count || 0
      }];
    }
    
    // If no filters applied and no profit_by_hotel data, return empty
    return [];
  };
  
  // Export function for recent bookings
  const exportRecentBookingsToExcel = () => {
    // Prepare data for export
    const exportData = dashboardData.recentBookings.map(booking => ({
      'Booking ID': booking.id || '',
      Customer: booking.customer || '',
      Hotel: booking.hotel || '',
      Date: booking.date || '',
      Amount: booking.amount ? `$${booking.amount}` : '$0',
      Status: booking.status || ''
    }));
    
    exportToExcel(exportData, 'recent_bookings', 'Recent Bookings');
  };

  // Handle loading states
  const isLoading = bookingsLoading || revenueLoading || trendsLoading || demographicsLoading || recentBookingsLoading;

  if (isLoading) {
    return <div className="loading">Loading dashboard data...</div>;
  }

  return (
    <div className="dashboard">
      <h1>Dashboard Overview</h1>
      
      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="card">
          <div className="card-header">
            <h3>Total Bookings</h3>
            <span className="card-icon"><FaCalendarAlt /></span>
          </div>
          <div className="card-body">
            <h2>{dashboardData.totalBookings}</h2>
            <p className="card-change positive">{dashboardData.totalBookingsChange} from last month</p>
          </div>
        </div>
        
        <div className="card">
          <div className="card-header">
            <h3>Total Hotels</h3>
            <span className="card-icon"><FaHotel /></span>
          </div>
          <div className="card-body">
            <h2>{dashboardData.totalHotels}</h2>
            <p className="card-change neutral">+{dashboardData.newHotelsCount} new hotels</p>
          </div>
        </div>
      </div>

      {/* Profit Section Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h2>Profit Analytics</h2>
          {appliedFilters.month || appliedFilters.year || appliedFilters.hotel_id ? (
            <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
              Applied filters: 
              {appliedFilters.month && `Month: ${new Date(2026, appliedFilters.month-1).toLocaleString('default', { month: 'long' })}`}
              {appliedFilters.year && `, Year: ${appliedFilters.year}`}
              {appliedFilters.hotel_id && `, Hotel ID: ${appliedFilters.hotel_id}`}
            </div>
          ) : (
            <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
              Showing all data
            </div>
          )}
        </div>
        <div>
          {profitLoading && <span style={{ color: '#666', marginRight: '10px' }}>Loading profit data...</span>}
          <button 
            onClick={() => {
              console.log('Manual refresh clicked');
              refetchProfit();
            }}
            className="btn btn-secondary"
            style={{ padding: '5px 10px', fontSize: '12px' }}
            disabled={profitLoading}
          >
            {profitLoading ? 'Loading...' : 'Refresh Data'}
          </button>
        </div>
      </div>
      

      
      {/* Profit Summary Cards */}
      <div className="summary-cards">
        {console.log('Rendering profit cards with data:', profitData)}
        {console.log('Profit summary:', profitData.summary)}
        {console.log('Direct values - Revenue:', profitData.summary.total_revenue, 'Profit:', profitData.summary.total_profit, 'Bookings:', profitData.summary.booking_count)}
        <div className="card">
          <div className="card-header">
            <h3>Total Revenue</h3>
            <span className="card-icon"><FaMoneyBillWave /></span>
          </div>
          <div className="card-body">
            <h2>${profitData.summary.total_revenue.toLocaleString()}</h2>
            <p className="card-change positive">From Customers</p>
          </div>
        </div>
        
        <div className="card highlight">
          <div className="card-header">
            <h3>Net Profit</h3>
            <span className="card-icon"><FaMoneyBillWave /></span>
          </div>
          <div className="card-body">
            <h2 className="positive">${profitData.summary.total_profit.toLocaleString()}</h2>
            <p className="card-change positive">Your Earnings</p>
          </div>
        </div>
        

      </div>
      
      {/* Charts Section */}
      <div className="charts-section">
        <div className="chart-container">
          <h3><FaChartBar /> Most Searched Destinations</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={dashboardData.mostSearched}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="searches" fill="#8884d8" name="Searches" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        
        <div className="chart-container">
          <h3><FaChartPie /> Bookings by Country</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={dashboardData.bookingsByCountry}
                cx="50%"
                cy="50%"
                labelLine={true}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
              >
                {dashboardData.bookingsByCountry.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Profit Charts Section */}
      <div className="charts-section">
        <div className="chart-container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3><FaChartBar /> {appliedFilters.hotel_id ? 'Selected Hotel Profit Details' : 'Profit & Bookings by Hotel'}</h3>
            <div style={{ display: 'flex', gap: '10px' }}>
              <select 
                value={profitFilters.month}
                onChange={(e) => handleProfitFilterChange('month', e.target.value)}
                style={{ padding: '5px 10px', borderRadius: '4px', border: '1px solid #ddd' }}
              >
                <option value="">All Months</option>
                {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => 
                  <option key={m} value={m}>{new Date(2026, m-1).toLocaleString('default', { month: 'long' })}</option>
                )}
              </select>
              
              <input 
                type="number" 
                placeholder="Year" 
                value={profitFilters.year}
                onChange={(e) => handleProfitFilterChange('year', e.target.value)}
                style={{ padding: '5px 10px', borderRadius: '4px', border: '1px solid #ddd', width: '100px' }}
              />
              
              <select 
                value={profitFilters.hotel_id}
                onChange={(e) => handleProfitFilterChange('hotel_id', e.target.value)}
                style={{ padding: '5px 10px', borderRadius: '4px', border: '1px solid #ddd', width: '150px' }}
                disabled={hotels.length === 0}
              >
                <option value="">All Hotels</option>
                {hotels.length > 0 ? (
                  hotels.map(hotel => (
                    <option key={hotel.id} value={hotel.id}>
                      {hotel.name_en || hotel.name || hotel.hotel_name || `Hotel ${hotel.id}`} 
                      {`(ID: ${hotel.id})`}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>Loading hotels...</option>
                )}
              </select>
              
              <button 
                onClick={applyProfitFilters}
                className="btn btn-primary"
                style={{ padding: '5px 15px', borderRadius: '4px' }}
              >
                Apply
              </button>
              
              <button 
                onClick={clearProfitFilters}
                className="btn btn-secondary"
                style={{ padding: '5px 15px', borderRadius: '4px' }}
              >
                Clear
              </button>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart 
              key={`profit-chart-${profitData.profit_by_hotel?.length || 0}-${appliedFilters.hotel_id || 'all'}`}
              data={getChartData()}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="hotel_name" 
                tick={{ fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis />
              <Tooltip 
                formatter={(value, name) => {
                  if (name === 'Profit') return [`$${parseFloat(value).toFixed(2)}`, 'Net Profit'];
                  if (name === 'Bookings') return [value, 'Bookings Count'];
                  return [`$${parseFloat(value).toFixed(2)}`, name];
                }}
                labelFormatter={(label) => `Hotel: ${label}`}
              />
              <Legend />
              <Bar dataKey="total_profit" fill="#4CAF50" name="Profit" />
              <Bar dataKey="booking_count" fill="#2196F3" name="Bookings" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      {/* Recent Activity */}
      <div className="recent-activity">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3>Recent Bookings</h3>
          <div>
            {recentBookingsLoading && <span style={{ color: '#666', fontSize: '14px' }}>Loading...</span>}
            {recentBookingsError && <span style={{ color: '#e74c3c', fontSize: '14px' }}>Error: {recentBookingsError.message}</span>}
            <button 
              className="btn btn-primary" 
              onClick={exportRecentBookingsToExcel}
            >
              Export to Excel
            </button>
          </div>
        </div>
        <table className="activity-table">
          <thead>
            <tr>
              <th>Booking ID</th>
              <th>Customer</th>
              <th>Hotel</th>
              <th>Date</th>
              <th>Amount</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {dashboardData.recentBookings && dashboardData.recentBookings.length > 0 ? (
              dashboardData.recentBookings.map(booking => (
                <tr key={booking.id || `booking-${Math.random()}`}>
                  <td>{booking.id || 'N/A'}</td>
                  <td>{booking.customer || 'N/A'}</td>
                  <td>{booking.hotel || 'N/A'}</td>
                  <td>{booking.date || 'N/A'}</td>
                  <td>{booking.amount ? `$${booking.amount}` : '$0'}</td>
                  <td>
                    <span className={`status ${booking.status ? booking.status.toLowerCase() : 'default'}`}>
                      {booking.status || 'N/A'}
                    </span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', fontStyle: 'italic', color: '#666' }}>
                  {recentBookingsLoading ? 'Loading bookings...' : 'No recent bookings found'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        
        {/* Debug Info - Temporary */}
        <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f9f9f9', border: '1px solid #eee', fontSize: '12px' }}>
          <strong>Debug Info:</strong> Recent bookings count: {dashboardData.recentBookings?.length || 0}, 
          Loading: {recentBookingsLoading ? 'true' : 'false'}, 
          Has error: {recentBookingsError ? 'true' : 'false'}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;